/**
 * Product additional detail service for API calls (product_additional_details table).
 * Part of the products domain; FK to products(id).
 */
import axios from 'axios';
import { authHeaders } from './apiAuth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Get all additional details for a product
 * @param {string} accessToken - User's access token
 * @param {number} productId - Product id
 * @returns {Promise<Object>} { success, product_additional_details }
 */
export const getProductAdditionalDetails = async (accessToken, productId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/product-additional-details`, {
      params: { product_id: productId },
      headers: authHeaders(accessToken)
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to get product additional details'
    );
  }
};

/**
 * Get additional details for a product that have mlb_attribute_id set (non-null).
 * @param {string} accessToken - User's access token
 * @param {number} productId - Product id
 * @returns {Promise<Object>} { success, product_additional_details }
 */
export const getProductAdditionalDetailsMlbAttributes = async (accessToken, productId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/product-additional-details/mlb-attributes`,
      {
        params: { product_id: productId },
        headers: authHeaders(accessToken)
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to get MLB attribute details'
    );
  }
};

/**
 * Get a single product additional detail by id
 * @param {string} accessToken - User's access token
 * @param {number} detailId - Additional detail id
 * @returns {Promise<Object>} { success, product_additional_detail }
 */
export const getProductAdditionalDetail = async (accessToken, detailId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/product-additional-details/${detailId}`,
      { headers: authHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to get product additional detail'
    );
  }
};

/**
 * Create a new product additional detail
 * @param {string} accessToken - User's access token
 * @param {Object} data - { product_id, detail_name?, detail_value?, detail_group? }
 * @returns {Promise<Object>} { success, product_additional_detail }
 */
export const createProductAdditionalDetail = async (accessToken, data) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/product-additional-details`,
      { product_additional_detail: data },
      { headers: authHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to create product additional detail'
    );
  }
};

/**
 * Update a product additional detail by id
 * @param {string} accessToken - User's access token
 * @param {number} detailId - Additional detail id
 * @param {Object} data - { detail_name?, detail_value?, detail_group? }
 * @returns {Promise<Object>} { success, product_additional_detail }
 */
export const updateProductAdditionalDetail = async (
  accessToken,
  detailId,
  data
) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/api/product-additional-details/${detailId}`,
      { product_additional_detail: data },
      { headers: authHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to update product additional detail'
    );
  }
};

/**
 * Delete a product additional detail by id
 * @param {string} accessToken - User's access token
 * @param {number} detailId - Additional detail id
 * @returns {Promise<Object>} { success, message }
 */
export const deleteProductAdditionalDetail = async (accessToken, detailId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/api/product-additional-details/${detailId}`,
      { headers: authHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to delete product additional detail'
    );
  }
};
